import {
  useMutation,
  useQuery,
  useQueryClient,
  UseQueryOptions,
} from '@tanstack/react-query'

import type {
  Agent,
  ApiError,
  CreateAgentRequest,
  ListAgentsParams,
  ListAgentsResponse,
  PartnerEnum,
  PauseResumeResponse,
  SyncJobResponse,
  UpdateAgentRequest,
} from '../groveSchedulerClient'
import type { GroveSchedulerClientInstance } from './useGroveSchedulerClient'

/**
 * Query key factory for integration agents
 * Provides consistent, hierarchical query keys for React Query cache management
 */
export const IntegrationAgentQueryKeys = {
  ALL: ['integrationAgents'] as const,
  LISTS: () => [...IntegrationAgentQueryKeys.ALL, 'list'] as const,
  LIST: (filters: Record<string, unknown>) =>
    [...IntegrationAgentQueryKeys.LISTS(), filters] as const,
  DETAILS: () => [...IntegrationAgentQueryKeys.ALL, 'detail'] as const,
  DETAIL: (id: string) => [...IntegrationAgentQueryKeys.DETAILS(), id] as const,
}

/**
 * Hook to list integration agents with optional filtering
 *
 * Backend automatically filters by authenticated user's company for customer users.
 * Internal users can optionally filter by companyId, partner, or active status.
 *
 * @param params - Query parameters
 * @param params.client - Grove scheduler client instance
 * @param params.companyId - Optional company filter (internal users only)
 * @param params.partner - Optional partner filter (e.g., 'jane', 'dentally')
 * @param params.active - Optional active status filter
 * @param params.limit - Optional pagination limit
 * @param params.offset - Optional pagination offset
 * @param params.onRequestCompleted - Success callback
 * @param params.onRequestErrored - Error callback
 * @param params.options - Additional React Query options
 *
 * @example
 * // Customer user - automatically filtered by their company
 * const client = useGroveSchedulerClient()
 * const { data, isLoading } = useListIntegrationAgents({ client })
 *
 * @example
 * // Internal user - filter by specific company
 * const client = useGroveSchedulerClient()
 * const { data } = useListIntegrationAgents({
 *   client,
 *   companyId: 'company-123',
 *   partner: 'jane'
 * })
 */
export const useListIntegrationAgents = ({
  client,
  companyId,
  partner,
  active,
  limit,
  offset,
  onRequestCompleted,
  onRequestErrored,
  options,
}: {
  client: GroveSchedulerClientInstance
  companyId?: string
  partner?: string
  active?: boolean
  limit?: number
  offset?: number
  onRequestCompleted?: (data: ListAgentsResponse) => void
  onRequestErrored?: (error: ApiError) => void
  options?: Omit<UseQueryOptions<ListAgentsResponse, ApiError>, 'queryKey'>
}) => {
  // Build params - note that 'active' is supported by backend but not yet in generated schema
  const params: ListAgentsParams & { active?: boolean } = {}
  if (companyId !== undefined) params.companyId = companyId
  // Type assertion needed until schema accepts all partner values
  if (partner !== undefined) params.partner = partner as PartnerEnum
  if (active !== undefined) params.active = active
  if (limit !== undefined) params.limit = limit
  if (offset !== undefined) params.offset = offset

  const query = useQuery<ListAgentsResponse, ApiError>({
    queryKey: IntegrationAgentQueryKeys.LIST({
      companyId,
      partner,
      active,
      limit,
      offset,
    }),
    queryFn: async () => {
      const { ok, data } = await client.listAgents(params)

      if (!ok) {
        const error = data as ApiError
        onRequestErrored?.(error)
        return Promise.reject(error)
      }

      const response = data as ListAgentsResponse
      onRequestCompleted?.(response)
      return response
    },
    retry: options?.retry ?? false,
    enabled: options?.enabled ?? true,
    ...options,
  })

  return query
}

/**
 * Hook to get a single integration agent by ID
 *
 * @param params - Query parameters
 * @param params.client - Grove scheduler client instance
 * @param params.agentId - The agent ID (UUID)
 * @param params.onRequestCompleted - Success callback
 * @param params.onRequestErrored - Error callback
 * @param params.options - Additional React Query options
 *
 * @example
 * const client = useGroveSchedulerClient()
 * const { data: agent } = useGetIntegrationAgent({
 *   client,
 *   agentId: 'agent-123'
 * })
 */
export const useGetIntegrationAgent = ({
  client,
  agentId,
  onRequestCompleted,
  onRequestErrored,
  options,
}: {
  client: GroveSchedulerClientInstance
  agentId: string
  onRequestCompleted?: (data: Agent) => void
  onRequestErrored?: (error: ApiError) => void
  options?: Omit<UseQueryOptions<Agent, ApiError>, 'queryKey'>
}) => {
  const query = useQuery<Agent, ApiError>({
    queryKey: IntegrationAgentQueryKeys.DETAIL(agentId),
    queryFn: async () => {
      const { ok, data } = await client.getAgent(agentId)

      if (!ok) {
        const error = data as ApiError
        onRequestErrored?.(error)
        return Promise.reject(error)
      }

      const agent = data as Agent
      onRequestCompleted?.(agent)
      return agent
    },
    retry: options?.retry ?? false,
    enabled: options?.enabled ?? !!agentId,
    ...options,
  })

  return query
}

/**
 * Hook to pause an integration agent (stop sync triggers)
 *
 * Automatically invalidates related queries on success.
 *
 * @param params - Client and callbacks
 * @param params.client - Grove scheduler client instance
 * @param params.onSuccess - Called when pause succeeds
 * @param params.onError - Called when pause fails
 *
 * @example
 * const client = useGroveSchedulerClient()
 * const pauseMutation = usePauseIntegrationAgent({
 *   client,
 *   onSuccess: (data, agentId) => {
 *     toast.success('Agent paused successfully')
 *   }
 * })
 *
 * pauseMutation.mutate('agent-123')
 */
export const usePauseIntegrationAgent = ({
  client,
  onSuccess,
  onError,
}: {
  client: GroveSchedulerClientInstance
  onSuccess?: (data: PauseResumeResponse, agentId: string) => void
  onError?: (error: ApiError, agentId: string) => void
}) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (agentId: string) => {
      const response = await client.pauseAgent(agentId)
      if (!response.ok) {
        throw response.data
      }
      return { agentId, data: response.data as PauseResumeResponse }
    },
    onSuccess: ({ data, agentId }) => {
      // Invalidate queries to refetch updated data
      queryClient.invalidateQueries({ queryKey: IntegrationAgentQueryKeys.ALL })
      onSuccess?.(data, agentId)
    },
    onError: (error: ApiError, agentId) => {
      onError?.(error, agentId)
    },
    retry: false,
  })
}

/**
 * Hook to resume an integration agent (restart sync triggers)
 *
 * Automatically invalidates related queries on success.
 *
 * @param params - Client and callbacks
 * @param params.client - Grove scheduler client instance
 * @param params.onSuccess - Called when resume succeeds
 * @param params.onError - Called when resume fails
 *
 * @example
 * const client = useGroveSchedulerClient()
 * const resumeMutation = useResumeIntegrationAgent({
 *   client,
 *   onSuccess: (data, agentId) => {
 *     toast.success('Agent resumed successfully')
 *   }
 * })
 *
 * resumeMutation.mutate('agent-123')
 */
export const useResumeIntegrationAgent = ({
  client,
  onSuccess,
  onError,
}: {
  client: GroveSchedulerClientInstance
  onSuccess?: (data: PauseResumeResponse, agentId: string) => void
  onError?: (error: ApiError, agentId: string) => void
}) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (agentId: string) => {
      const response = await client.resumeAgent(agentId)
      if (!response.ok) {
        throw response.data
      }
      return { agentId, data: response.data as PauseResumeResponse }
    },
    onSuccess: ({ data, agentId }) => {
      // Invalidate queries to refetch updated data
      queryClient.invalidateQueries({ queryKey: IntegrationAgentQueryKeys.ALL })
      onSuccess?.(data, agentId)
    },
    onError: (error: ApiError, agentId) => {
      onError?.(error, agentId)
    },
    retry: false,
  })
}

/**
 * Hook to create a new integration agent
 *
 * Automatically invalidates list queries on success.
 *
 * @param params - Client and callbacks
 * @param params.client - Grove scheduler client instance
 * @param params.onSuccess - Called when creation succeeds
 * @param params.onError - Called when creation fails
 *
 * @example
 * const client = useGroveSchedulerClient()
 * const createMutation = useCreateIntegrationAgent({
 *   client,
 *   onSuccess: (agent) => {
 *     toast.success('Agent created successfully')
 *   }
 * })
 *
 * createMutation.mutate({
 *   agentId: 'agent-123',
 *   companyId: 'company-456',
 *   partner: 'jane',
 *   config: { clinic_url: 'https://example.janeapp.com' }
 * })
 */
export const useCreateIntegrationAgent = ({
  client,
  onSuccess,
  onError,
}: {
  client: GroveSchedulerClientInstance
  onSuccess?: (data: Agent, variables: CreateAgentRequest) => void
  onError?: (error: ApiError, variables: CreateAgentRequest) => void
}) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (request: CreateAgentRequest) => {
      const response = await client.createAgent(request)
      if (!response.ok) {
        throw response.data
      }
      return response.data as Agent
    },
    onSuccess: (data, variables) => {
      // Invalidate queries to refetch updated list
      queryClient.invalidateQueries({ queryKey: IntegrationAgentQueryKeys.ALL })
      onSuccess?.(data, variables)
    },
    onError: (error: ApiError, variables) => {
      onError?.(error, variables)
    },
    retry: false,
  })
}

/**
 * Hook to update an existing integration agent
 *
 * Automatically invalidates related queries on success.
 *
 * @param params - Client and callbacks
 * @param params.client - Grove scheduler client instance
 * @param params.onSuccess - Called when update succeeds
 * @param params.onError - Called when update fails
 *
 * @example
 * const client = useGroveSchedulerClient()
 * const updateMutation = useUpdateIntegrationAgent({
 *   client,
 *   onSuccess: (agent) => {
 *     toast.success('Agent updated successfully')
 *   }
 * })
 *
 * updateMutation.mutate({
 *   agentId: 'agent-123',
 *   request: { config: { clinic_url: 'https://new.janeapp.com' } }
 * })
 */
export const useUpdateIntegrationAgent = ({
  client,
  onSuccess,
  onError,
}: {
  client: GroveSchedulerClientInstance
  onSuccess?: (
    data: Agent,
    variables: { agentId: string; request: UpdateAgentRequest },
  ) => void
  onError?: (
    error: ApiError,
    variables: { agentId: string; request: UpdateAgentRequest },
  ) => void
}) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({
      agentId,
      request,
    }: {
      agentId: string
      request: UpdateAgentRequest
    }) => {
      const response = await client.updateAgent(agentId, request)
      if (!response.ok) {
        throw response.data
      }
      return response.data as Agent
    },
    onSuccess: (data, variables) => {
      // Invalidate queries to refetch updated data
      queryClient.invalidateQueries({ queryKey: IntegrationAgentQueryKeys.ALL })
      onSuccess?.(data, variables)
    },
    onError: (error: ApiError, variables) => {
      onError?.(error, variables)
    },
    retry: false,
  })
}

/**
 * Hook to trigger a sync job for an integration agent
 *
 * @param params - Client and callbacks
 * @param params.client - Grove scheduler client instance
 * @param params.onSuccess - Called when sync job starts successfully
 * @param params.onError - Called when sync job fails to start
 *
 * @example
 * const client = useGroveSchedulerClient()
 * const syncMutation = useCreateSyncJob({
 *   client,
 *   onSuccess: (response) => {
 *     toast.success(`Sync job ${response.jobId} started`)
 *   }
 * })
 *
 * syncMutation.mutate('agent-123')
 */
export const useCreateSyncJob = ({
  client,
  onSuccess,
  onError,
}: {
  client: GroveSchedulerClientInstance
  onSuccess?: (data: SyncJobResponse, agentId: string) => void
  onError?: (error: ApiError, agentId: string) => void
}) => {
  return useMutation({
    mutationFn: async (agentId: string) => {
      const response = await client.createSyncJob(agentId)
      if (!response.ok) {
        throw response.data
      }
      return { agentId, data: response.data as SyncJobResponse }
    },
    onSuccess: ({ data, agentId }) => {
      onSuccess?.(data, agentId)
    },
    onError: (error: ApiError, agentId) => {
      onError?.(error, agentId)
    },
    retry: false,
  })
}
